/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { Observable, Subscription } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { CAGroup, CAGroupRequest, CASpecialPlanning, CAWeekDay, CAWeeklySchedule } from 'src/app/model/control-access';
import { selectGroupModifing, selectGroupModify, selectSpecialPlanningsAll, selectWeeklySchedulesAll } from 'src/app/store/device/device.selector';
import { CustomValidators } from 'src/app/validators/custom.validators';

@Component({
	selector: 'app-control-access-group-easy-dialog',
	templateUrl: './control-access-group-easy-dialog.component.html',
})

export class ControlAccessGroupEasyDialogDialogComponent implements OnInit, OnDestroy {

	groupsModify$: Observable<boolean | undefined>;
	groupsModifySub: Subscription | undefined;
	groupsModifing$: Observable<boolean | undefined>;
	groupsModifingSub: Subscription | undefined;
	weeklySchedulesAll$: Observable<CAWeeklySchedule[] | undefined>;
	weeklySchedulesAllSub: Subscription | undefined;
	specialPlanningsAll$: Observable<CASpecialPlanning[] | undefined>;
	specialPlanningsAllSub: Subscription | undefined;

	group: CAGroup;
	editMode = false;
	saving = false;

	groupForm: FormGroup;

	editedSub: Subscription;
	addedSub: Subscription;
	groupModifySub: Subscription;

	listWeeklySchedule: CAWeeklySchedule[];
	listWeeklyScheduleSub: Subscription;
	weeklyScheduleCtrl = new FormControl();
	filteredWeeklySchedule: Observable<CAWeeklySchedule[]>;

	listSpecialPlanning: CASpecialPlanning[];
	listSpecialPlanningSub: Subscription;
	specialPlanningCtrl = new FormControl();
	filteredSpecialPlanning: Observable<CASpecialPlanning[]>;

	// design
	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
	) {
		this.group = new CAGroup();
		if (config.data !== undefined && config.data.group !== undefined) {
			this.group = config.data.group;
			this.editMode = true;
		} else {
			this.group.WeeklySchedules = [];
			this.group.SpecialPlannings = [];
		}
		this.initSelectors();
	}

	initSelectors(): void {
		this.groupsModify$ = this.store.select(selectGroupModify);
		this.groupsModifing$ = this.store.select(selectGroupModifing);
		this.weeklySchedulesAll$ = this.store.select(selectWeeklySchedulesAll);
		this.specialPlanningsAll$ = this.store.select(selectSpecialPlanningsAll);
	}

	ngOnInit(): void {
		this.groupForm = this.fb.group({
			'Name': new FormControl(this.group.Name, [Validators.required, Validators.maxLength(48)]),
			'WeeklySchedule': new FormControl(this.group.WeeklySchedules),
			'SpecialPlanning': new FormControl(this.group.SpecialPlannings),
		});
		this.groupForm.addValidators(CustomValidators.checkOneElemOneLists('WeeklySchedule', 'SpecialPlanning'));

		this.store.dispatch(DeviceActions.getWeeklySchedulesAll());
		this.store.dispatch(DeviceActions.getSpecialPlanningsAll());

		this.subscribeAll();

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	subscribeAll(): void {
		this.groupsModifySub = this.groupsModify$
			.pipe(filter(groupsModify => !!groupsModify))
			.subscribe(() => {
				this.resetClose();
		});
		this.groupsModifingSub = this.groupsModifing$
			.subscribe((editing) => {
				this.saving = editing;
		});

		this.weeklySchedulesAllSub = this.weeklySchedulesAll$.subscribe(listWeeklySchedule => {
			this.listWeeklySchedule = listWeeklySchedule;
		});
		this.specialPlanningsAllSub = this.specialPlanningsAll$.subscribe(listSpecialPlanning => {
			this.listSpecialPlanning = listSpecialPlanning;
		});

		this.filteredWeeklySchedule = this.weeklyScheduleCtrl.valueChanges.pipe(
			map((weeklySchedule: string | null) => weeklySchedule ? this.filterWeeklySchedule(weeklySchedule) : this.listWeeklySchedule.slice())
		);
		this.filteredSpecialPlanning = this.specialPlanningCtrl.valueChanges.pipe(
			map((specialPlanning: string | null) => specialPlanning ? this.filterSpecialPlanning(specialPlanning) : this.listSpecialPlanning.slice())
		);
	}

	ngOnDestroy(): void {
		this.groupsModifySub?.unsubscribe();
		this.groupsModifingSub?.unsubscribe();
		this.weeklySchedulesAllSub?.unsubscribe();
		this.specialPlanningsAllSub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetGroupForm());
		this.closeDialog();
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.groupForm.controls).forEach(key => {
			if (key !== 'WeeklySchedule' && key !== 'SpecialPlanning') {
				const controlErrors: ValidationErrors = this.groupForm.get(key).errors;
				if (controlErrors != null) {
					/* Object.keys(controlErrors).forEach(keyError => {
						console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
					}); */
					atLeastOneError = true;
				}
			}
		});
		let wsSpErr = this.showDoubleErrorRequiredMessageAndCheck(this.groupForm.value.WeeklySchedule, this.groupForm.value.SpecialPlanning)
		atLeastOneError = atLeastOneError || wsSpErr;
		return atLeastOneError;
	}

	save(): void {
		const group = new CAGroupRequest();
		group.Name = this.f.Name.value;
		group.WeeklySchedules = [];
		group.SpecialPlannings = [];

		if (this.f.WeeklySchedule.value !== null && this.f.WeeklySchedule.value !== undefined) {
			for (const wees of this.f.WeeklySchedule.value) {
				group.WeeklySchedules.push(wees.Id)
			}
		}
		if (this.f.SpecialPlanning.value !== null && this.f.SpecialPlanning.value !== undefined) {
			for (const sppl of this.f.SpecialPlanning.value) {
				group.SpecialPlannings.push(sppl.Id)
			}
		}
		if (this.group.Id) {
			group.Id = this.group.Id;
			this.store.dispatch(DeviceActions.editGroup({ group }));
		} else {
			this.store.dispatch(DeviceActions.addGroup({ group }));
		}
	}

	filterWeeklySchedule(value: string): CAWeeklySchedule[] {
		if (typeof value === 'string') {
			const filterValue = value.toLowerCase();
			return this.listWeeklySchedule.filter(weeklySchedule => weeklySchedule.Name.toLowerCase().indexOf(filterValue) >= 0);
		}
		return this.listWeeklySchedule;
	}

	filterSpecialPlanning(value: string): CASpecialPlanning[] {
		if (typeof value === 'string') {
			const filterValue = value.toLowerCase();
			return this.listSpecialPlanning.filter(specialPlanning => specialPlanning.Name.toLowerCase().indexOf(filterValue) >= 0);
		}
		return this.listSpecialPlanning;
	}

	removeWeeklySchedule(weeklySchedule: CAWeeklySchedule): void {
		const index = this.group.WeeklySchedules.indexOf(weeklySchedule);

		if (index >= 0) {
			this.group.WeeklySchedules.splice(index, 1);
		}
	}

	removeSpecialPlanning(specialPlanning: CASpecialPlanning): void {
		const index = this.group.SpecialPlannings.indexOf(specialPlanning);

		if (index >= 0) {
			this.group.SpecialPlannings.splice(index, 1);
		}
	}

	daysOfWeekSchedule(weeklySchedule: CAWeeklySchedule): CAWeekDay[] {
		const currentWeekDays = [
			{ Value: '0', Name: 'MON', Enable: false },
			{ Value: '1', Name: 'TUE', Enable: false },
			{ Value: '2', Name: 'WED', Enable: false },
			{ Value: '3', Name: 'THU', Enable: false },
			{ Value: '4', Name: 'FRI', Enable: false },
			{ Value: '5', Name: 'SAT', Enable: false },
			{ Value: '6', Name: 'SUN', Enable: false },
		];
		const ppp = [];
		let value = weeklySchedule.Weekdays;
		for (let i = 6; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				ppp.unshift(i.toString());
				currentWeekDays[i].Enable = true;
			}
			value = value % (2 ** i);
		}
		return currentWeekDays;
	}

	get f(): any {
		return this.groupForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	getWidthLabelWs(weeklySchedules: CAWeeklySchedule[]): string {
		let numChar = 5;
		for (const ws of weeklySchedules) {
			if (ws.Name.length > numChar) {
				numChar = ws.Name.length;
			}
		}
		return 'width: ' + numChar * 9 + 'px;';

	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-23rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: ControlAccessGroupEasyDialogDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: ControlAccessGroupEasyDialogDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	showDoubleErrorRequiredMessageAndCheck(typeWs: ControlAccessGroupEasyDialogDialogComponent["f"], typeSp: ControlAccessGroupEasyDialogDialogComponent["f"]): boolean {
		return StandardNewValidator.showDoubleErrorRequiredMessageAndCheck(typeWs, typeSp);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

}
